import type {
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
  Event,
  EventHint,
} from '@sentry/browser';

const sentryBrowserPromise: Promise<
  Pick<
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    typeof import('@sentry/browser'),
    | 'BrowserClient'
    | 'defaultStackParser'
    | 'captureException'
    | 'captureMessage'
    | 'getCurrentHub'
    | 'GlobalHandlers'
    | 'InboundFilters'
    | 'FunctionToString'
    | 'TryCatch'
    | 'Breadcrumbs'
    | 'LinkedErrors'
    | 'HttpContext'
    | 'Dedupe'
    | 'makeFetchTransport'
  >
> = import(
  /* webpackChunkName: "sentry" */
  /* webpackExports: ["BrowserClient", "defaultStackParser", "captureException", "captureMessage", "getCurrentHub", "GlobalHandlers", "InboundFilters", "FunctionToString", "TryCatch", "Breadcrumbs", "LinkedErrors", "HttpContext", "Dedupe", "makeFetchTransport"] */
  '@sentry/browser'
);

const sentryTracingPromise: Promise<
  Pick<
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    typeof import('@sentry/tracing'),
    'BrowserTracing'
  >
> = import(
  /* webpackChunkName: "sentry-tracing" */
  /* webpackExports: ["BrowserTracing"] */
  '@sentry/tracing'
);

const sentryIntegrationsPromise: Promise<
  Pick<
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    typeof import('@sentry/integrations'),
    'RewriteFrames'
  >
> = import(
  /* webpackChunkName: "sentry-integrations" */
  /* webpackExports: ["RewriteFrames"] */
  '@sentry/integrations'
);

function createRejectedSentryPromise() {
  const promise = new Promise<never>((_resolve, reject) =>
    // Make sure the promise is rejected after the .catch() handler is attached,
    // otherwise an unnecessary console error message will be logged
    // eslint-disable-next-line no-promise-executor-return
    requestAnimationFrame(reject)
  );
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  promise.catch(() => {});
  return promise;
}

export const sentryPromise =
  typeof window !== 'undefined' && window.SENTRY
    ? Promise.all([
        sentryBrowserPromise,
        sentryTracingPromise,
        sentryIntegrationsPromise,
      ]).then(([sentryBrowser, sentryTracing, sentryIntegrations]) => ({
        ...sentryBrowser,
        ...sentryTracing,
        ...sentryIntegrations,
      }))
    : createRejectedSentryPromise();

const ignoreErrorsSubstrings = [
  'Hydration failed',
  'error while hydrating',
  'Blocked a frame with origin "https://www.prisjakt.nu" from accessing a cross-origin frame',
];

function shouldIgnoreError(error: Event, hint: EventHint): boolean {
  if (
    typeof hint.originalException === 'object' &&
    typeof hint.originalException?.message === 'string'
  ) {
    const { message } = hint.originalException;
    if (ignoreErrorsSubstrings.some(sub => message.includes(sub))) {
      return true;
    }
  }

  if ((error as any).status === 404 || (error as any).status === 410) {
    return true;
  }

  return false;
}

export function initSentry() {
  sentryPromise
    .then(
      ({
        BrowserClient,
        defaultStackParser,
        getCurrentHub,
        GlobalHandlers,
        InboundFilters,
        FunctionToString,
        TryCatch,
        Breadcrumbs,
        LinkedErrors,
        HttpContext,
        Dedupe,
        makeFetchTransport,
        BrowserTracing,
        RewriteFrames,
      }) => {
        // See https://docs.sentry.io/platforms/javascript/configuration/tree-shaking/ for what is happening
        const client = new BrowserClient({
          // debug is defined in webpack.config.js with DefinePlugin
          dsn: window.SENTRY,
          release: window.RELEASE,
          tracesSampleRate: 0.001,
          transport: makeFetchTransport,
          stackParser: defaultStackParser,
          beforeSend: (event, hint) => {
            return shouldIgnoreError(event, hint) ? null : addEventTags(event);
          },
          beforeSendTransaction: addEventTags,
          integrations: [
            new BrowserTracing(),
            new GlobalHandlers(),
            new InboundFilters(),
            new FunctionToString(),
            new TryCatch(),
            new Breadcrumbs(),
            new LinkedErrors(),
            new HttpContext(),
            new Dedupe(),
            new RewriteFrames({
              root: 'webpack://@prisjakt/responsive-frontend/./',
              prefix: 'rfe-browser:///',
            }),
          ],
          allowUrls: [
            /^https:\/\/pjrfe-75b8\.kxcdn\.com/,
            /^https:\/\/www\.prisjakt\.(nu|no)/,
            /^https:\/\/pricespy\.co\.(uk|nz)/,
            /^https:\/\/prisjagt\.dk/,
            /^https:\/\/hintaopas\.fi/,
            /^https:\/\/ledenicheur\.fr/,
          ],
        });

        getCurrentHub().bindClient(client);
      }
    )
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    .catch(() => {});
}

function addEventTags<E extends Event>(event: E): E {
  // eslint-disable-next-line no-param-reassign
  event.tags = event.tags ?? {};

  // eslint-disable-next-line no-param-reassign
  event.tags.market = window.CLIENT_MARKET.code;

  // eslint-disable-next-line no-param-reassign
  event.tags.useBff = window.USE_BFF_SERVICE;

  return event;
}

// Used by sagas
export function captureException(
  ...args: Parameters<typeof sentryCaptureException>
) {
  sentryPromise
    .then(({ captureException: sentryCaptureException }) =>
      sentryCaptureException(...args)
    )
    .catch(() => {
      // eslint-disable-next-line no-console
      console.error(...args);
    });
}

// Used by sagas
export function captureMessage(
  ...args: Parameters<typeof sentryCaptureMessage>
) {
  sentryPromise
    .then(({ captureMessage: sentryCaptureMessage }) =>
      sentryCaptureMessage(...args)
    )
    .catch(() => {
      // eslint-disable-next-line no-console
      console.log(...args);
    });
}
